import { supabase } from '../lib/supabase';

export const REPORTING_SUPABASE_URL =
  (import.meta as any).env?.VITE_REPORTING_SUPABASE_URL || 'https://uydhsjvwrgupgfjevqsz.supabase.co';
export const REPORTING_SUPABASE_ANON_KEY =
  (import.meta as any).env?.VITE_REPORTING_SUPABASE_ANON_KEY ||
  'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

export interface TikTokReconcileResult {
  /** adgroup the campaign's existing ads live in — required to add more. */
  adgroupId: string | null;
  /** an existing Smart+ ad id, kept only so the release row has one. */
  adId: string | null;
  /** auth codes already live on TikTok, so we never re-add them. */
  liveAuthCodes: Set<string>;
  /** local ad_creatives ids we matched to a live material. */
  matchedCreativeIds: string[];
  /** number of local rows whose ids we backfilled. */
  backfilled: number;
}

const empty = (): TikTokReconcileResult => ({
  adgroupId: null,
  adId: null,
  liveAuthCodes: new Set<string>(),
  matchedCreativeIds: [],
  backfilled: 0,
});

/**
 * Bring the local DB in sync with what actually exists in a TikTok campaign
 * before we add anything to it.
 *
 * This is the safety step for bulk adding. The campaign may already contain
 * Spark Ad materials that were created outside this app (or by an earlier run
 * that failed to write back), and locally those rows still have a NULL
 * tiktok_ad_id. Without reconciling, "pending" is computed as "has an auth
 * code and no tiktok_ad_id", so every already-live creative looks pending and
 * would be pushed a second time.
 *
 * It only ever reads from TikTok and writes ids back into our own tables — it
 * never creates, edits or deletes anything on TikTok.
 */
export async function reconcileTikTokCampaign(
  releaseId: string,
  campaignId: string,
): Promise<TikTokReconcileResult> {
  const response = await fetch(
    `${REPORTING_SUPABASE_URL}/functions/v1/get-tiktok-smart-plus-ads`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ campaign_id: campaignId }),
    },
  );

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || data?.error || 'Failed to read TikTok campaign');
  }

  const ads: any[] = Array.isArray(data?.ads) ? data.ads : [];
  const result = empty();
  if (ads.length === 0) return result;

  result.adgroupId = ads.find((a) => a.adgroup_id)?.adgroup_id || null;
  result.adId = ads.find((a) => a.smart_plus_ad_id)?.smart_plus_ad_id || null;

  // Backfill every local row we can match to a live material, so the
  // de-duplication key (tiktok_ad_id) becomes meaningful.
  const updates: Array<Promise<any>> = [];
  for (const ad of ads) {
    for (const material of ad.creative_list || []) {
      const authCode = material?.matched_creative?.auth_code;
      if (authCode) result.liveAuthCodes.add(String(authCode));

      const localId = material?.matched_creative?.id;
      if (!localId) continue;
      result.matchedCreativeIds.push(String(localId));
      updates.push(
        supabase
          .from('ad_creatives' as any)
          .update({
            // Mark as active so the creative no longer shows as "pending" in
            // the UI — it is live on TikTok, which is what tiktok_ad_id
            // already records. Without this the bulk-add modal correctly
            // excludes the creative (isTikTokEligible checks tiktok_ad_id)
            // but the list still shows it as pending, confusing the user.
            status: 'active',
            tiktok_ad_status: 'ACTIVE',
            tiktok_campaign_id: campaignId,
            tiktok_adgroup_id: ad.adgroup_id ?? null,
            tiktok_ad_id: ad.smart_plus_ad_id ?? null,
            tiktok_ad_material_id: material.ad_material_id ?? null,
            tiktok_item_id: material.tiktok_item_id ?? null,
          } as any)
          .eq('id' as any, localId as any) as unknown as Promise<any>,
      );
    }
  }

  const settled = await Promise.allSettled(updates);
  result.backfilled = settled.filter((s) => s.status === 'fulfilled').length;

  // Give the release the adgroup/ad ids so adding a creative works even when
  // the campaign was linked by hand (the link modal only writes campaign id).
  if (result.adgroupId || result.adId) {
    await supabase
      .from('releases')
      .update({
        tiktok_adgroup_id: result.adgroupId,
        tiktok_ad_id: result.adId,
      } as any)
      .eq('id' as any, releaseId as any);
  }

  return result;
}
